<template>
  <SectionCard :id="section.id" :title="section.title">
    <div class="grid gap-6">
      <!-- Uppgifter om bostaden -->
      <div :id="section.subSections.details.id" class="property-facts-container">
        <h3 class="mb-2">
          {{ section.subSections.details.title }}
        </h3>

        <div v-if="collateral" class="property-card rounded-lg p-4">
          <div class="mb-4">
            <h4>{{ formatAddress(collateral.address) }}</h4>
            <p class="text-subtle">{{ collateral.address.zip ?? '-' }} {{ collateral.address.city }}</p>
          </div>

          <dl class="property-fields gap-x-6 gap-y-3">
            <div v-for="field in propertyFacts" :key="field.label">
              <dt class="text-xs font-medium uppercase text-subtle">{{ field.label }}</dt>
              <dd class="mt-1">{{ field.value }}</dd>
            </div>
          </dl>
        </div>
        <p v-else class="text-subtle">Ingen bostadsinformation.</p>
      </div>

      <!-- Värdering -->
      <div :id="section.subSections.valuations.id">
        <h3 class="mb-2">
          {{ section.subSections.valuations.title }}
        </h3>

        <AppTable v-if="valuations.length">
          <thead>
            <tr>
              <th>Värde</th>
              <th>Tillagd</th>
              <th>Källa</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="(valuation, index) in valuations" :key="index">
              <AppTableCell nowrap>{{ formatAmountWithCurrency(valuation.value) }}</AppTableCell>
              <AppTableCell nowrap>{{ formatDateIfValid(valuation.valuationDate) ?? '-' }}</AppTableCell>
              <AppTableCell>{{ valuation.type || '-' }}</AppTableCell>
              <AppTableCell>
                <AppChip v-if="valuation.isCurrent" color="brand">Nuvarande</AppChip>
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Ingen värdering gjord.</p>

        <h4 class="mt-4 mb-2">Dokument</h4>

        <AppTable>
          <thead>
            <tr>
              <th>Dokumenttyp</th>
              <th>Uppladdningsdatum</th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="valuationDocuments?.length">
            <AppTableRow v-for="valuation in valuationDocuments" :key="valuation.id">
              <AppTableCell>{{ translateDocumentType(valuation.type) }}</AppTableCell>
              <AppTableCell>{{ formatDateIfValid(valuation.createdAt) }}</AppTableCell>
              <AppTableCell action>
                <AppButton
                  icon
                  small
                  :loading="openingDocumentId === valuation.id"
                  :aria-label="`Visa ${translateDocumentType(valuation.type)}`"
                  title="Visa"
                  @click="openDocument(valuation)"
                >
                  <SvgIcon :path="mdiMagnify" :size="18" />
                </AppButton>
              </AppTableCell>
            </AppTableRow>
          </tbody>

          <tbody v-else>
            <AppTableRow>
              <AppTableCell colspan="3" class="text-center"> Inga dokument tillagda </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </div>

      <!-- Försäkring -->
      <div :id="section.subSections.insurance.id">
        <h3 class="mb-2">
          {{ section.subSections.insurance.title }}
        </h3>

        <AppTable v-if="insurance">
          <thead>
            <tr>
              <th>Försäkrad</th>
              <th>Bolag</th>
              <th>Helförsäkrad</th>
              <th>Försäkringsbelopp</th>
              <th>Självrisk</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow>
              <AppTableCell>{{ formatBooleanValue(insurance.isInsured) }}</AppTableCell>
              <AppTableCell>{{ insuranceCompanyLabel ?? '-' }}</AppTableCell>
              <AppTableCell>{{ formatBooleanValue(insurance.isFullyInsured) }}</AppTableCell>
              <AppTableCell nowrap>{{ formatAmountWithCurrency(insurance.coverageAmount) }}</AppTableCell>
              <AppTableCell nowrap>{{ formatAmountWithCurrency(insurance.excessAmount) }}</AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Ingen försäkringsinformation.</p>
      </div>

      <!-- Lagfart (house/vacation only) -->
      <div v-if="houseOrVacation" :id="section.subSections.titleDeed.id">
        <h3 class="mb-2">
          {{ section.subSections.titleDeed.title }}
        </h3>

        <div v-if="titleDeed" class="title-deed flex flex-col gap-5 rounded-md p-4">
          <!-- Fastigheten och dess flaggor -->
          <div>
            <p v-if="titleDeed.propertyLabel" class="font-semibold">
              {{ titleDeed.propertyLabel.toUpperCase() }}
            </p>
            <p v-if="titleDeed.titleDeedType" class="text-xs text-subtle">{{ titleDeed.titleDeedType }}</p>

            <div v-if="titleDeedWarnings.length" class="warning-card">
              <p class="font-medium">Varningar i lagfarten:</p>
              <p v-for="(warning, index) in titleDeedWarnings" :key="index" class="font-medium">{{ warning }}</p>
            </div>
          </div>

          <!-- Sammanfattning -->
          <div v-if="titleDeed.summary">
            <h4 class="mb-1">Sammanfattning</h4>
            <p class="deed-text">{{ titleDeed.summary }}</p>
          </div>

          <!-- Ägare -->
          <div>
            <h4 class="mb-2">Ägare</h4>

            <div v-if="titleDeed.owners.length" class="flex flex-col gap-3">
              <article v-for="(owner, index) in titleDeed.owners" :key="index" class="deed-entry rounded-md p-3">
                <p class="font-medium">
                  {{ owner.name }}
                  <span v-if="owner.isLegalEntity" class="text-xs text-subtle">(juridisk person)</span>
                </p>

                <dl class="deed-fields mt-2 gap-x-6 gap-y-2">
                  <div v-for="row in ownerDetails(owner)" :key="row.label">
                    <dt class="text-xs uppercase text-subtle">{{ row.label }}</dt>
                    <dd class="mt-0.5">{{ row.value }}</dd>
                  </div>
                </dl>

                <!-- Övriga förvärv -->
                <div v-if="owner.otherAcquisitions.length" class="mt-3">
                  <p class="text-xs font-medium uppercase text-subtle">Övriga förvärv</p>
                  <ul class="deed-list mt-1">
                    <li v-for="(acquisition, acquisitionIndex) in owner.otherAcquisitions" :key="acquisitionIndex">
                      {{ describeAcquisition(acquisition) }}
                    </li>
                  </ul>
                </div>

                <!-- Anmärkningar -->
                <div v-if="owner.remarks.length" class="mt-3">
                  <p class="text-xs font-medium uppercase text-subtle">Anmärkningar</p>
                  <div v-for="(remark, remarkIndex) in owner.remarks" :key="remarkIndex" class="deed-remark mt-2 pl-3">
                    <dl class="deed-fields gap-x-6 gap-y-1">
                      <div v-for="row in remarkDetails(remark)" :key="row.label">
                        <dt class="text-xs uppercase text-subtle">{{ row.label }}</dt>
                        <dd class="mt-0.5">{{ row.value }}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </article>
            </div>
            <p v-else class="text-subtle">Inga ägare registrerade.</p>
          </div>

          <!-- Noteringar -->
          <div v-if="titleDeed.notes.length">
            <h4 class="mb-2">Noteringar</h4>
            <div
              v-for="(note, index) in titleDeed.notes"
              :key="index"
              class="deed-entry mt-2 rounded-md p-3 first:mt-0"
            >
              <p v-if="note.date" class="text-xs text-subtle">{{ formatDateIfValid(note.date) }}</p>
              <p v-if="note.note" class="deed-text mt-0.5">{{ note.note }}</p>
              <p v-if="note.editedRemarkText" class="deed-text mt-1 text-subtle">
                Redigerad text: {{ note.editedRemarkText }}
              </p>
            </div>
          </div>
        </div>
        <p v-else class="text-subtle">Ingen lagfartsinformation.</p>
      </div>

      <!-- BRF (apartment only) -->
      <div v-if="isApartment" :id="section.subSections.housingAssociation.id">
        <h3 class="mb-2">
          {{ section.subSections.housingAssociation.title }}
        </h3>

        <AppTable v-if="housingAssociation">
          <thead>
            <tr>
              <th>Namn</th>
              <th>Adress</th>
              <th>Org.nr</th>
              <th v-if="housingAssociation.administrator">Förvaltare</th>
              <th v-if="housingAssociation.housingAssociationApartmentNumber">BRF lägenhetsnummer</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow>
              <AppTableCell>{{ housingAssociation.name }}</AppTableCell>
              <AppTableCell>
                {{ housingAssociation.address.street }}, {{ housingAssociation.address.zip }}
                {{ housingAssociation.address.city }}
              </AppTableCell>
              <AppTableCell>{{ housingAssociation.organisationNumber }}</AppTableCell>
              <AppTableCell v-if="housingAssociation.administrator">
                {{ housingAssociation.administrator.name }}
              </AppTableCell>
              <AppTableCell v-if="housingAssociation.housingAssociationApartmentNumber">
                {{ housingAssociation.housingAssociationApartmentNumber }}
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Ingen information om bostadsrättsförening.</p>
      </div>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SectionCard, AppTable, AppTableRow, AppTableCell, AppButton, AppChip, SvgIcon } from '@staff/ui'
import { formatAmountWithCurrency, formatBooleanValue, formatDateIfValid } from '@staff/shared'
import type { ApplicationSections } from './sections'
import type { TitleDeedOwner, TitleDeedOwnerAcquisition, TitleDeedOwnerRemark, ValuationType } from '@/services/application'
import { formatAddress } from '@/utils/formatAddress'
import { isRealEstateCollateral } from '@/utils/applicationDisplay'
import { useRequiredApplication } from '@/views/application/application.context'
import { getDocumentsByType, useApplicationDocumentsContext } from '@/views/application/applicationDocuments.context'
import { useDocumentPreview } from '@/composables/documentPreview'
import { mdiMagnify } from '@mdi/js'
import { useMetadata } from '@/composables/useMetadata'

defineProps<{
  section: ApplicationSections['property']
}>()

const application = useRequiredApplication()
const { t } = useI18n()
const { translateDocumentType, getReferenceValues } = useMetadata()
const { documents } = useApplicationDocumentsContext()
const { openingDocumentId, openDocument } = useDocumentPreview((document) => {
  return translateDocumentType(document.type)
})

const valuationDocuments = computed(() => getDocumentsByType(documents.value, 'propertyValue'))

type DisplayRow = { label: string; value: string }

const optionalRow = <T,>(
  label: string,
  value: T | null | undefined,
  format?: (value: NonNullable<T>) => string | null | undefined,
): DisplayRow[] => {
  if (value == null) return []
  return [{ label, value: (format ? format(value as NonNullable<T>) : String(value)) ?? '-' }]
}

const collateral = computed(() => application.value.collateralProperty)
const houseOrVacation = computed(() => isRealEstateCollateral(collateral.value))
const isApartment = computed(() => collateral.value?.type === 'apartment')

// TODO: Add valuation translation to metadata
const translateValuationType = (type: ValuationType) => {
  switch (type) {
    case 'automated':
      return 'Statistisk värdering'
    case 'manual':
      return 'Mäklarvärdering'
    case 'other':
      return 'Annan'
    default:
      return type
  }
}

// Nuvarande värdering först, därefter tidigare i den ordning backend skickar dem.
const valuations = computed(() => {
  const { currentValuation, previousValuations } = collateral.value?.valuations ?? { previousValuations: [] }

  return [
    ...(currentValuation
      ? [{ ...currentValuation, type: translateValuationType(currentValuation.type), isCurrent: true }]
      : []),
    ...previousValuations.map((valuation) => ({
      ...valuation,
      type: translateValuationType(valuation.type),
      isCurrent: false,
    })),
  ]
})
const titleDeed = computed(() => (collateral.value && houseOrVacation.value ? collateral.value.titleDeed : null))

const titleDeedWarnings = computed(() => [
  ...(titleDeed.value?.isLeasehold ? ['Fastigheten är en tomträtt'] : []),
  ...(titleDeed.value?.hasOngoingCase ? ['Fastighetsinskrivningsärende pågår'] : []),
  ...(titleDeed.value?.hasJointMortgageDeeds ? ['Fastigheten är samintecknad'] : []),
  ...(titleDeed.value?.hasDeregisteredOwner ? ['Ägare avregistrerad från folkbokföringen'] : []),
  ...(titleDeed.value?.hasLimitations ? ['Förbehåll finns i lagfarten'] : []),
])

const propertyOverview = computed<DisplayRow[]>(() => {
  const cp = collateral.value
  if (!cp) return []

  return [
    { label: 'Typ av bostad', value: t(`PROPERTY_TYPE.${cp.type}`) },
    { label: 'Kommun', value: cp.address.municipality?.name ?? '-' },
    ...(houseOrVacation.value
      ? [
          { label: 'Fastighetsbeteckning', value: cp.propertyLabel ?? '-' },
          { label: 'Taxeringsvärde', value: formatAmountWithCurrency(cp.taxValue) },
        ]
      : []),
    ...optionalRow('Uppskattad värdering', application.value.propertyValueEstimation, formatAmountWithCurrency),
  ]
})

const propertyDetails = computed<DisplayRow[]>(() => {
  const cp = collateral.value
  if (!cp) return []

  const isApartment = cp.type === 'apartment'

  return [
    {
      label: isApartment ? 'Driftskostnad inkl. avgift' : 'Driftskostnad',
      value: cp.monthlyCost == null ? '-' : `${formatAmountWithCurrency(cp.monthlyCost)}/mån`,
    },
    { label: 'Boarea', value: cp.size == null ? '-' : `${cp.size} kvm` },
    ...optionalRow('Antal rum', cp.rooms),
    ...(isApartment
      ? [
          ...optionalRow('Våning', cp.floors),
          ...optionalRow('Antal våningar i huset', cp.nbrOfFloors),
          ...optionalRow('Balkong', cp.balcony, formatBooleanValue),
          ...optionalRow('Hiss', cp.elevator, formatBooleanValue),
        ]
      : optionalRow('Antal våningar', cp.nbrOfFloors ?? cp.floors)),
    ...optionalRow('Nyproduktion', cp.isNewProduction, formatBooleanValue),
    { label: 'Energiklass', value: cp.energyDeclaration?.energyClass ?? '-' },
    ...optionalRow('Energideklaration', cp.energyDeclaration?.createdAt, formatDateIfValid),
  ]
})

const propertyFacts = computed<DisplayRow[]>(() => [...propertyOverview.value, ...propertyDetails.value])

// Lagfarten kommer från Lantmäteriet och fälten är glest ifyllda — bara det som har ett värde listas
// TODO: translations should probably come from metadata?
const ownerDetails = (owner: TitleDeedOwner): DisplayRow[] => [
  ...(owner.isLegalEntity
    ? optionalRow('Org.nr', owner.orgNo)
    : optionalRow('Födelsedatum', owner.birthDate, (birthDate) => formatDateIfValid(String(birthDate)))),
  ...optionalRow('Andel', owner.ownershipShare),
  ...optionalRow('Civilstånd', owner.maritalStatus),
  ...optionalRow('Typ av ägande', owner.typeOfOwnership),
  ...optionalRow('Förvärvat genom', owner.mainAcquisition?.acquisitionType),
  ...optionalRow('Förvärvsdatum', owner.mainAcquisition?.acquisitionDate, formatDateIfValid),
  ...optionalRow('Beslut', owner.mainAcquisition?.decision),
  ...optionalRow('Anmärkning på förvärv', owner.mainAcquisition?.remark),
  ...(owner.deregistrated
    ? [
        { label: 'Avregistrerad', value: formatDateIfValid(owner.deregistrationDate) ?? 'Ja' },
        ...optionalRow('Orsak', owner.deregistrationReason),
      ]
    : []),
]

const remarkDetails = (remark: TitleDeedOwnerRemark): DisplayRow[] => [
  ...optionalRow('Ärende', remark.caseId),
  ...optionalRow('Diarienummer', remark.diaryNo),
  ...optionalRow('Beslut', remark.decision),
  ...optionalRow('Anmärkning', remark.remark),
  ...optionalRow('Övrig anmärkning', remark.otherRemark),
  ...optionalRow('Delad bostad', remark.isSharedResidence, formatBooleanValue),
  ...optionalRow('Tvist löst', remark.disputeResolvedFor),
]

const describeAcquisition = (acquisition: TitleDeedOwnerAcquisition) =>
  [
    acquisition.acquisitionType,
    formatDateIfValid(acquisition.acquisitionDate),
    acquisition.decision,
    acquisition.remark,
  ]
    .filter(Boolean)
    .join(' · ') || '-'

const insurance = computed(() => collateral.value?.insurance)
const insuranceCompanyLabel = computed(() => {
  const company = insurance.value?.insuranceCompany
  if (!company) return undefined
  if (company === 'OTHER') return insurance.value?.insuranceCompanyOtherName ?? 'Annat'
  return getReferenceValues('insuranceCompanies').find((item) => item.code === company)?.label ?? company
})

const housingAssociation = computed(() => {
  const cp = collateral.value
  if (cp?.type === 'apartment') return cp.housingAssociation
  return undefined
})
</script>

<style scoped lang="scss">
.warning-card {
  border: 1px solid var(--color-warning-fg);
  background-color: var(--color-warning-subtle);
  padding: 0.5rem;
  border-radius: 0.375rem;
  color: var(--color-warning-on-subtle);
  width: fit-content;

  p:not(:first-child) {
    margin-top: 0.5rem;
  }
}

.property-facts-container {
  container-type: inline-size;
}

.property-card {
  border: 1px solid var(--color-border-muted);
  background-color: var(--color-canvas-inset);
}

.property-fields {
  display: grid;
  grid-template-columns: 1fr;
}

@container (min-width: 30rem) {
  .property-fields {
    grid-template-columns: 1fr 1fr;
  }
}

@container (min-width: 48rem) {
  .property-fields {
    grid-template-columns: repeat(3, 1fr);
  }
}

.title-deed {
  container-type: inline-size;
  border: 1px solid var(--color-border-muted);
  background-color: var(--color-canvas-default);
}

.deed-entry {
  background-color: var(--color-canvas-inset);
}

.deed-remark {
  border-left: 2px solid var(--color-border-default);
}

// Fritext från Lantmäteriet — behåll radbrytningar.
.deed-text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.deed-list {
  list-style: disc;
  padding-left: 1.25rem;
}

.deed-fields {
  display: grid;
  grid-template-columns: 1fr;
}

@container (min-width: 30rem) {
  .deed-fields {
    grid-template-columns: 1fr 1fr;
  }
}

@container (min-width: 48rem) {
  .deed-fields {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
